import { useEffect, useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Activity, BarChart3, Newspaper, Sigma } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import { AnalyticsNote, ExpandableDisclaimer } from "@/components/Compliance";
import usePageMeta from "@/hooks/usePageMeta";
import { COMPANY } from "@/lib/site";

const QUANT_IMG = "https://static.prod-images.emergentagent.com/jobs/ce6902cc-1297-4975-a372-ff71f8243192/images/7798f7e8ebbc003801a399301e6a57d79b0493baccf791e91c1772e360ce1303.png";
const HEDGE_IMG = "https://static.prod-images.emergentagent.com/jobs/ce6902cc-1297-4975-a372-ff71f8243192/images/833ba591ef330f41ec0063d5a621125c52eb0d25e76227b2ab6c100421aed507.png";

export default function Landing() {
  usePageMeta({
    title: "Adviserz — AI-Powered Market Intelligence & Financial Technology Platform",
    description:
      "Adviserz, a brand of Quant Core Technologies Private Limited, builds quantitative research infrastructure, derivatives analytics and AI-powered market intelligence. Informational and analytical purposes only — not investment advice.",
    canonical: "https://adviserz.in/",
  });
  useScrollReveal();
  return (
    <div className="min-h-screen bg-surface-base text-white selection:bg-ai-accent/30">
      <SiteHeader active="home" />
      <Hero />
      <WhyNow />
      <Capabilities />
      <ComparisonSection />
      <Democratisation />
      <MarketOpportunity />
      <PlatformAccessTeaser />
      <FoundersSection />
      <LeadCapture />
      <SiteFooter />
      <MobileStickyCTA />
    </div>
  );
}

/* ============================ scroll reveal ============================ */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("is-visible");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ============================ HERO ============================ */
function Hero() {
  return (
    <section className="relative min-h-screen overflow-hidden bg-surface-base">
      <div className="absolute inset-0">
        <div className="absolute inset-0 grid-bg grid-radial-mask" />
        <HeroCanvas />
        <div className="absolute inset-0 spotlight-column" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pt-32 pb-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline mb-6 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-ai-accent animate-pulse" />
              Financial Technology · Market Intelligence
            </div>
            <h1 className="font-display font-medium text-[42px] sm:text-6xl lg:text-[74px] leading-[1.03] tracking-tighter">
              AI-Powered <br />
              <span className="brand-text-gradient">Market Intelligence.</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Quantitative research, machine learning and market analytics built to help market participants understand
              financial markets with greater depth.
            </p>
            <p className="mt-4 text-sm text-zinc-500 max-w-2xl leading-relaxed">
              {COMPANY.disclosure}
            </p>

            <div className="mt-11 flex flex-wrap gap-3">
              <Link to="/technology" data-testid="hero-explore"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-md brand-gradient text-white font-medium hover:brightness-110 transition-all shadow-[0_0_40px_-8px_rgba(0,184,255,0.55)]">
                Explore Our Technology <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/early-access" data-testid="hero-early-access"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md border border-white/15 bg-white/[0.03] backdrop-blur-md text-white font-medium hover:bg-white/[0.07] hover:border-white/25 transition-colors">
                Join Early Access
              </Link>
            </div>

            <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-zinc-500">
              <span className="overline text-zinc-500">Focus areas</span>
              <span className="text-zinc-300">Quantitative Research</span>
              <span className="text-zinc-600">·</span>
              <span className="text-zinc-300">Derivatives Analytics</span>
              <span className="text-zinc-600">·</span>
              <span className="text-zinc-300">Volatility Analytics</span>
              <span className="text-zinc-600">·</span>
              <span className="text-zinc-300">News Intelligence</span>
            </div>
          </div>

          <div className="md:col-span-5">
            <AbstractPanel />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, t = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    const draw = () => {
      const w = canvas.offsetWidth, h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      const pulse = (Math.sin(t / 60) + 1) / 2;
      const g1 = ctx.createRadialGradient(w * 0.35, h * 0.45, 0, w * 0.35, h * 0.45, w * 0.5);
      g1.addColorStop(0, `rgba(37,99,235,${0.16 + pulse * 0.1})`);
      g1.addColorStop(1, "rgba(37,99,235,0)");
      ctx.fillStyle = g1;
      ctx.fillRect(0, 0, w, h);
      const g2 = ctx.createRadialGradient(w * 0.75, h * 0.6, 0, w * 0.75, h * 0.6, w * 0.45);
      g2.addColorStop(0, `rgba(112,0,255,${0.1 + (1 - pulse) * 0.08})`);
      g2.addColorStop(1, "rgba(112,0,255,0)");
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, w, h);
      t++;
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-90" />;
}

/** Abstract, non-numeric representation of the analytics pipeline. No market data, no figures. */
function AbstractPanel() {
  const stages = ["Data Ingestion", "Feature Engineering", "Model Layer", "Analytics Output"];
  return (
    <div className="relative reveal" data-testid="hero-visual">
      <div className="absolute -inset-6 ambient-glow blur-2xl" />
      <div className="relative surface-elevated rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="overline text-zinc-400">Platform Architecture</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600">Illustrative</div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-12 gap-1.5">
            {Array.from({ length: 48 }).map((_, i) => {
              const r = (Math.sin(i * 1.7) + 1) / 2;
              const hue = r > 0.6 ? "0,184,255" : r > 0.35 ? "37,99,235" : "112,0,255";
              return (
                <div key={i} className="aspect-square rounded-[2px]" style={{ background: `rgba(${hue}, ${0.12 + r * 0.4})` }} />
              );
            })}
          </div>

          <div className="mt-5 space-y-2">
            {stages.map((s, i) => (
              <div key={s} className="flex items-center gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
                <span className="font-mono text-[10px] text-ai-accent">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-xs text-zinc-300">{s}</span>
                <span className="ml-auto h-1 w-16 rounded-full bg-white/[0.06] overflow-hidden">
                  <span className="block h-full brand-gradient" style={{ width: `${40 + i * 15}%` }} />
                </span>
              </div>
            ))}
          </div>

          <p className="mt-5 text-[11px] text-zinc-600 leading-relaxed">
            Abstract representation of the platform's analytics pipeline. Not live market data and not a depiction of
            any security, price or recommendation.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============================ WHY NOW ============================ */
function WhyNow() {
  const stats = [
    { n: "170M+", l: "Demat accounts in India", src: "NSDL · CDSL 2025", danger: false },
    { n: "<2,000", l: "SEBI-registered advisory firms in India", src: "SEBI registry", danger: false },
    { n: "93%", l: "Retail derivatives traders incurred losses FY22–24", src: "SEBI 2024 study", danger: true },
    { n: "₹1.8L Cr+", l: "Aggregate retail derivatives losses over 3 years", src: "SEBI · Aggregated", danger: true },
  ];
  return (
    <section className="relative bg-surface-base border-t border-white/[0.06]">
      <div className="absolute inset-0 grid-bg-fine opacity-60 grid-radial-mask" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-36">
        <div className="max-w-3xl reveal">
          <div className="overline mb-6">01 · Why Now</div>
          <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.08]">
            India's market participation grew. <br />
            <span className="text-zinc-500">Access to analytics did not.</span>
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-white/[0.08] divide-y sm:divide-y-0 sm:divide-x divide-white/[0.06]">
          {stats.map((s, i) => (
            <div key={i} className="p-8 reveal" style={{ transitionDelay: `${i * 80}ms` }}>
              <div className={`font-mono text-4xl md:text-5xl tracking-tight ${s.danger ? "text-destructive" : "text-white"}`}>{s.n}</div>
              <div className="mt-4 text-sm text-zinc-300 leading-relaxed">{s.l}</div>
              <div className="mt-3 overline text-zinc-500">{s.src}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 max-w-3xl reveal">
          <p className="text-[11px] text-zinc-600 leading-relaxed">
            Figures are drawn from publicly available regulatory and depository disclosures and are cited for context
            only. They are not a representation about any outcome from using this platform.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10 reveal">
          <p className="lg:col-span-7 text-zinc-400 leading-relaxed text-base md:text-lg">
            India has become one of the world's largest retail market ecosystems. Yet market participants still rely
            heavily on fragmented information channels, while structured research data, quantitative analytics and
            market-structure intelligence remain largely concentrated in institutional hands.
          </p>
          <div className="lg:col-span-5 border-l border-white/10 pl-8">
            <p className="font-display text-2xl md:text-3xl tracking-tight leading-snug text-white">
              Participation scaled. <br />
              <span className="text-zinc-500">Analytical infrastructure did not.</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ CAPABILITIES ============================ */
function Capabilities() {
  const features = [
    { icon: Sigma, t: "Quantitative Research Engine", d: "Statistical analysis, time-series research, feature engineering and backtesting infrastructure applied to market and derivatives data.", img: QUANT_IMG, to: "/quant-research" },
    { icon: Activity, t: "Derivatives & Options Analytics", d: "Options chain analytics, open interest analytics, volatility analytics and market-structure analysis — measurement, not prescription.", img: null, to: "/quant-research" },
    { icon: BarChart3, t: "Risk & Scenario Analytics", d: "Probability and scenario analysis, exposure analytics and volatility regime measurement under clearly stated assumptions.", img: HEDGE_IMG, to: "/market-intelligence" },
    { icon: Newspaper, t: "AI News Intelligence", d: "News aggregation, sentiment analysis, entity extraction, market-event detection and AI-assisted summarisation.", img: null, to: "/news-intelligence" },
  ];
  return (
    <section id="infrastructure" className="relative bg-surface-base border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-36">
        <div className="max-w-3xl reveal">
          <div className="overline mb-6">02 · What we build</div>
          <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.08]">
            Quantitative research and <br />
            <span className="brand-text-gradient">market analytics technology.</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-2xl">
            Advanced analytics, quantitative research infrastructure and market intelligence tools for market
            participants — built as technology, not as advice.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Link key={f.t} to={f.to} data-testid={`feature-card-${i}`}
                className="trace-border rounded-none p-8 md:p-10 reveal block" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="flex items-start gap-5">
                  <div className="shrink-0 w-11 h-11 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-ai-accent" />
                  </div>
                  <div className="flex-1">
                    <div className="font-mono text-[11px] text-zinc-500 tracking-[0.2em]">0{i + 1}</div>
                    <h3 className="mt-1 font-display font-medium text-xl md:text-2xl tracking-tight flex items-center gap-2">
                      {f.t} <ArrowUpRight className="w-4 h-4 text-zinc-600" />
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-zinc-400 leading-relaxed">{f.d}</p>
                  </div>
                </div>
                {f.img && (
                  <div className="mt-8 relative h-40 rounded-md overflow-hidden border border-white/[0.06]">
                    <img src={f.img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-transparent to-transparent" />
                  </div>
                )}
              </Link>
            );
          })}
        </div>

        <div className="mt-10 max-w-3xl reveal">
          <AnalyticsNote />
        </div>
      </div>
    </section>
  );
}

/* ============================ COMPARISON ============================ */
function ComparisonSection() {
  const rows = [
    ["Fragmented tips and forwarded messages", "Structured, documented analytics"],
    ["Opinion without measurement", "Quantitative measurement with context"],
    ["Disconnected tools", "Unified research infrastructure"],
    ["Unstated assumptions", "Assumptions and limitations disclosed"],
    ["Outcome claims", "Probability and scenario framing"],
  ];
  return (
    <section className="relative bg-surface-base border-t border-white/[0.06]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-28 md:py-36">
        <div className="max-w-3xl reveal">
          <div className="overline mb-6">03 · The Shift</div>
          <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.08]">
            From informal information <br />
            <span className="text-zinc-500">to structured analytics.</span>
          </h2>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/[0.08] border border-white/[0.08] rounded-xl overflow-hidden reveal">
          <div className="bg-surface px-6 md:px-10 py-6 border-b border-white/[0.06]">
            <div className="overline text-zinc-500">Informal information channels</div>
          </div>
          <div className="relative bg-[#0A0F1E] px-6 md:px-10 py-6 border-b border-white/[0.06]">
            <div className="absolute inset-x-0 top-0 h-px brand-gradient" />
            <div className="overline text-ai-accent">Adviserz · Analytics technology</div>
          </div>
          {rows.map(([a, b], i) => (
            <Fragment key={i}>
              <div className="bg-surface px-6 md:px-10 py-6 text-zinc-500 text-base md:text-lg">{a}</div>
              <div className="bg-[#0A0F1E] px-6 md:px-10 py-6 text-white text-base md:text-lg font-medium">{b}</div>
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ DEMOCRATISATION ============================ */
function Democratisation() {
  return (
    <section className="relative bg-surface-base border-t border-white/[0.06] overflow-hidden">
      <div className="absolute inset-0 ambient-glow" />
      <div className="absolute inset-0 grid-bg-fine opacity-40 grid-radial-mask" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-32 md:py-44 text-center reveal">
        <div className="overline mb-6 justify-center">04 · Access</div>
        <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.08]">
          Quantitative analytics <br />
          <span className="text-zinc-500">has historically been institutional.</span>
        </h2>
        <p className="mt-10 text-zinc-400 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
          Derivatives analytics, volatility research, market-structure measurement and quantitative research systems
          have largely been built for institutional desks. Adviserz is building this class of analytical technology as
          accessible software — so that market participants can study markets with the same analytical rigour.
        </p>
        <blockquote className="mt-16 font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.1]">
          Markets are moving from opinion <br className="hidden md:block" />
          <span className="brand-text-gradient">to measurement.</span>
        </blockquote>
      </div>
    </section>
  );
}

/* ============================ MARKET OPPORTUNITY ============================ */
function MarketOpportunity() {
  const tiers = [
    { k: "TAM", label: "Total Addressable Market", inr: "₹80,000+ Cr", usd: "~USD 9.5B+", desc: "170M+ demat accounts; one of the world's largest retail market ecosystems.", width: "100%", color: "from-[#0F2342] to-[#0F2342]/40" },
    { k: "SAM", label: "Serviceable Addressable", inr: "₹8,000–10,000 Cr", usd: "~USD 1B–1.2B", desc: "Active market participants seeking structured analytics and research tooling.", width: "62%", color: "from-[#1E4FCC] to-[#1E4FCC]/40" },
    { k: "SOM", label: "Serviceable Obtainable", inr: "₹120–150 Cr+", usd: "~USD 14M–18M", desc: "Initial focus on engaged market participants using analytics and research software.", width: "36%", color: "from-[#00B8FF] to-[#7000FF]" },
  ];
  return (
    <section className="relative bg-surface-base border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-36">
        <div className="max-w-3xl reveal">
          <div className="overline mb-6">05 · Market Context</div>
          <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.08]">
            A large financial <br />
            <span className="text-zinc-500">technology opportunity.</span>
          </h2>
          <p className="mt-6 text-sm text-zinc-500 leading-relaxed">
            Company-level market sizing estimates, shown for corporate and informational context only. These are
            internal estimates, not projections of revenue, and not investment information of any kind.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-4 reveal">
            {tiers.map((t, i) => (
              <div key={t.k} className="relative" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-sm text-ai-accent">{t.k}</span>
                    <span className="text-xs text-zinc-500">{t.label}</span>
                  </div>
                  <div className="font-mono text-base text-white">{t.inr} <span className="text-zinc-500">· {t.usd}</span></div>
                </div>
                <div className="relative h-16 rounded-md border border-white/[0.08] bg-white/[0.02] overflow-hidden">
                  <div className={`absolute inset-y-0 left-0 bg-gradient-to-r ${t.color}`} style={{ width: t.width }} />
                  <div className="absolute inset-0 grid-bg-fine opacity-30" />
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-5 space-y-6 reveal">
            {tiers.map((t) => (
              <div key={t.k} className="border-l-2 border-white/10 pl-5">
                <div className="font-mono text-xs text-ai-accent">{t.k}</div>
                <div className="mt-1 text-sm text-zinc-300 leading-relaxed">{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ PLATFORM ACCESS TEASER ============================ */
function PlatformAccessTeaser() {
  return (
    <section id="platform-access" className="relative bg-surface-base border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-36">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 reveal">
            <div className="overline mb-6">06 · Platform Access</div>
            <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.08]">
              Adviserz Platform <br />
              <span className="brand-text-gradient">— Coming Soon.</span>
            </h2>
            <p className="mt-7 text-zinc-400 leading-relaxed">
              We are currently developing our next-generation market intelligence platform. Product availability and
              commercial offerings will be announced following completion of applicable regulatory and compliance
              requirements.
            </p>
            <p className="mt-4 text-sm text-zinc-500 leading-relaxed">
              Nothing is available for purchase on this website today. No subscription, signal service or advisory
              package is offered, and no payment is collected.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/pricing" data-testid="landing-pricing-link"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/15 text-white text-sm hover:bg-white/[0.05] transition-colors">
                View planned capability tiers <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/early-access" data-testid="landing-early-access-link"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md brand-gradient text-white text-sm font-medium hover:brightness-110 transition-all">
                Register Interest
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 grid gap-3 reveal">
            {[
              { t: "Equity Analytics", d: "Market intelligence, quantitative screening, market-structure analysis" },
              { t: "Equity + Derivatives Analytics", d: "Options chain, open interest and volatility analytics" },
              { t: "Full Intelligence Suite", d: "Scenario analysis, news intelligence, advanced research tooling" },
            ].map((p, i) => (
              <div key={p.t} className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-6 flex items-start justify-between gap-6">
                <div>
                  <div className="font-mono text-[11px] text-zinc-500 tracking-[0.2em]">0{i + 1}</div>
                  <div className="mt-1 font-display font-medium text-lg tracking-tight">{p.t}</div>
                  <div className="mt-2 text-sm text-zinc-500 leading-relaxed">{p.d}</div>
                </div>
                <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 border border-white/10 rounded-sm px-2 py-1">
                  Coming Soon
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ FOUNDERS ============================ */
function FoundersSection() {
  const founders = [
    { name: "Hitesh Diwedi", role: "Co-Founder & CEO", initials: "HD", bio: "Leads vision, product strategy and platform direction, with a focus on making quantitative analytics understandable for market participants." },
    { name: "Gurjeet Singh", role: "Co-Founder & COO", initials: "GS", bio: "Drives operations, execution and organisational scaling across engineering, research, product and go-to-market." },
  ];
  return (
    <section id="team" className="relative bg-surface-base border-t border-white/[0.06]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-28 md:py-36">
        <div className="max-w-3xl reveal">
          <div className="overline mb-6">07 · Founding Team</div>
          <h2 className="font-display font-medium text-3xl md:text-5xl tracking-tight leading-[1.08]">
            Builders behind <br />
            <span className="text-zinc-500">the analytics layer.</span>
          </h2>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-5">
          {founders.map((f, i) => (
            <div key={f.name} data-testid={`founder-${i}`}
              className="trace-border rounded-xl p-8 md:p-10 reveal" style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="flex items-start gap-6">
                <div className="shrink-0 w-20 h-20 rounded-xl brand-gradient flex items-center justify-center font-display font-medium text-2xl tracking-tight text-white shadow-[0_0_40px_-12px_rgba(0,184,255,0.55)]">
                  {f.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-medium text-2xl tracking-tight">{f.name}</h3>
                  <div className="mt-1 overline text-ai-accent">{f.role}</div>
                  <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{f.bio}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 reveal">
          <p className="text-xs text-zinc-500">
            {COMPANY.brand} is a brand of {COMPANY.legalName}. Registered office: {COMPANY.address}.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================ LEAD CAPTURE ============================ */
function LeadCapture() {
  return (
    <section id="early-access" className="relative bg-surface-base border-t border-white/[0.06] overflow-hidden">
      <div className="absolute inset-0 spotlight" />
      <div className="absolute inset-0 grid-bg opacity-40 grid-radial-mask" />
      <div className="relative max-w-2xl mx-auto px-6 md:px-12 py-28 md:py-36 reveal">
        <div className="overline mb-6 justify-center text-center">08 · Early Access</div>
        <h2 className="text-center font-display font-medium text-4xl md:text-5xl tracking-tight leading-[1.08]">
          Join early access.
        </h2>
        <p className="mt-6 text-center text-zinc-400 max-w-md mx-auto">
          Register your interest and we'll notify you when platform access opens. No payment is collected.
        </p>

        <EarlyAccessForm />

        <div className="mt-12">
          <ExpandableDisclaimer testid="landing-disclaimer" />
        </div>

        <div className="mt-8 text-center text-xs text-zinc-500">
          Looking to join the team instead? <Link to="/careers" className="text-white hover:underline">See open roles →</Link>
        </div>
      </div>
    </section>
  );
}

/* ============================ MOBILE STICKY CTA ============================ */
function MobileStickyCTA() {
  return (
    <div data-testid="mobile-sticky-cta"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] bg-surface-base/85 backdrop-blur-md border-t border-white/10">
      <a href="#early-access" data-testid="mobile-sticky-cta-btn"
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md brand-gradient text-white font-medium shadow-[0_0_30px_-10px_rgba(0,184,255,0.6)]">
        Join Early Access <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
}
